// Fork: edit a timeline note's time, public visibility and status label.
import { useState, type FormEvent } from 'react'
import { Pencil } from 'lucide-react'
import { updateTimelineNote } from '../../api/timeline'
import type { TimelineEntry } from '../../api/types'
import { Button } from '../ui/Button'
import { NoteTimeInput, StatusLabelInput } from './NoteFields'
import { fromLocalInputValue, toLocalInputValue } from './noteTime'
import { readableNoteText } from './noteRevisions'

export interface NoteActions {
  incidentId: string
  onUpdated: () => void
  onSuccess: (message: string) => void
  onError: (message: string) => void
}

export function TimelineNoteEditor({ entry, actions }: { entry: TimelineEntry; actions: NoteActions }) {
  const wasPublic = entry.type === 'public_update'
  const initialLabel = typeof entry.content?.status_label === 'string' ? entry.content.status_label : ''
  const [editing, setEditing] = useState(false)
  const [time, setTime] = useState(toLocalInputValue(entry.timestamp))
  const [isPublic, setIsPublic] = useState(wasPublic)
  const [label, setLabel] = useState(initialLabel)
  const initialMessage = readableNoteText(entry)
  const [message, setMessage] = useState(initialMessage)
  const [saving, setSaving] = useState(false)

  function open() {
    setTime(toLocalInputValue(entry.timestamp))
    setIsPublic(wasPublic)
    setLabel(initialLabel || (wasPublic ? '' : 'Investigating'))
    setMessage(initialMessage)
    setEditing(true)
  }

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!time) {
      actions.onError('Choose a time for this note')
      return
    }
    if (!message.trim()) {
      actions.onError('The message cannot be empty')
      return
    }
    setSaving(true)
    try {
      await updateTimelineNote(actions.incidentId, entry.id, {
        timestamp: fromLocalInputValue(time),
        public: isPublic,
        status_label: isPublic ? label.trim() : initialLabel,
        ...(message.trim() !== initialMessage.trim() ? { message: message.trim() } : {}),
      })
      actions.onSuccess(isPublic === wasPublic ? 'Note updated' : isPublic ? 'Note is now a public update' : 'Note is now internal')
      setEditing(false)
      actions.onUpdated()
    } catch (error) {
      actions.onError(error instanceof Error ? error.message : 'Failed to update note')
    } finally {
      setSaving(false)
    }
  }

  if (!editing) {
    return (
      <button type="button" onClick={open} className="mt-2 inline-flex items-center gap-1 text-xs text-text-tertiary hover:text-text-primary">
        <Pencil className="w-3 h-3" aria-hidden="true" />
        Edit
      </button>
    )
  }

  return (
    <form onSubmit={save} className="mt-3 pt-3 border-t border-border space-y-2">
      <label className="block text-sm text-text-secondary">
        Message
        <textarea
          value={message}
          onChange={e => setMessage(e.target.value)}
          disabled={saving}
          rows={3}
          maxLength={10000}
          className="mt-1 w-full px-3 py-2 border border-border rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary resize-y disabled:opacity-50"
        />
      </label>
      <NoteTimeInput value={time} onChange={setTime} disabled={saving} />
      <label className="flex items-center gap-2 text-sm text-text-secondary">
        <input type="checkbox" checked={isPublic} onChange={e => setIsPublic(e.target.checked)} disabled={saving} className="rounded border-border" />
        Public update
      </label>
      {isPublic && <StatusLabelInput value={label} onChange={setLabel} disabled={saving} />}
      <p className="text-xs text-text-tertiary">
        {isPublic
          ? 'Shown on the status page when the incident is public. Changes here are not posted to Slack, and the original message in Slack is unchanged.'
          : 'Internal notes never appear on the status page.'}
      </p>
      <div className="flex gap-2">
        <Button type="submit" size="sm" loading={saving}>Save</Button>
        <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(false)} disabled={saving}>Cancel</Button>
      </div>
    </form>
  )
}
